import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, CheckCircle, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { supabase } from '../../../lib/supabaseClient';

/**
 * MatchingSettings - Configure AI prompts and matching preferences
 */
const MatchingSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    rfq_ai_prompt: '',
    outbound_email_mode: 'draft',
    gmail_from_address: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    // Auto-dismiss success message
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: settingsError } = await supabase
        .from('recruiter_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }

      if (data) {
        setSettings(data);
        setFormData({
          rfq_ai_prompt: data.rfq_ai_prompt || '',
          outbound_email_mode: data.outbound_email_mode || 'draft',
          gmail_from_address: data.gmail_from_address || ''
        });
      } else {
        // Create default settings
        const { data: newSettings, error: createError } = await supabase
          .from('recruiter_settings')
          .insert({ user_id: user.id })
          .select()
          .single();

        if (createError) throw createError;

        setSettings(newSettings);
        setFormData({
          rfq_ai_prompt: newSettings.rfq_ai_prompt || '',
          outbound_email_mode: newSettings.outbound_email_mode || 'draft',
          gmail_from_address: newSettings.gmail_from_address || ''
        });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('recruiter_settings')
        .update({
          rfq_ai_prompt: formData.rfq_ai_prompt,
          outbound_email_mode: formData.outbound_email_mode,
          gmail_from_address: formData.gmail_from_address,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setSuccess('Settings saved successfully');
      await fetchSettings(); // Refresh
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    const defaultPrompt = `Analyze the following RFQ and extract matching criteria in JSON format.

Output MUST be valid JSON with this exact structure:
{
  "min_clearance_level": "NV1" | "NV2" | "TSPV" | "Baseline" | "None",
  "clearance_requirement_type": "ability_to_obtain",
  "required_skills": ["Skill 1", "Skill 2"],
  "nice_to_have_skills": ["Skill 3"],
  "target_locations": ["Canberra", "Sydney"],
  "min_experience_level": "beginner" | "intermediate" | "professional" | "expert"
}

RFQ Details:
{{RFQ_JSON}}

Return ONLY the JSON object, no explanatory text.`;

    setFormData({
      ...formData,
      rfq_ai_prompt: defaultPrompt
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Matching Settings</h2>
        <p className="text-muted-foreground">
          Configure AI prompts and default behavior for candidate matching
        </p>
      </div>

      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* AI Prompt Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Sparkles className="h-5 w-5 mr-2 text-indigo-600" />
            RFQ Analysis AI Prompt
          </CardTitle>
          <CardDescription>
            Customize the prompt sent to OpenAI for analyzing RFQs and extracting matching criteria
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-2">
              AI Prompt Template
            </label>
            <textarea
              value={formData.rfq_ai_prompt}
              onChange={(e) => setFormData({ ...formData, rfq_ai_prompt: e.target.value })}
              rows={15}
              className="w-full border rounded-md px-3 py-2 bg-background font-mono text-sm"
              placeholder="Enter AI prompt..."
            />
            <p className="text-xs text-muted-foreground mt-2">
              Use <code className="bg-muted px-1 py-0.5 rounded">{'{{RFQ_JSON}}'}</code> as a placeholder for the complete RFQ data.
              The AI will receive all RFQ fields as JSON.
            </p>
          </div>
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={resetToDefault}
            >
              Reset to Default
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Email Outreach Settings</CardTitle>
          <CardDescription>
            Configure default behavior for email outreach campaigns
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-2">
              Default Email Mode
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="draft"
                  checked={formData.outbound_email_mode === 'draft'}
                  onChange={(e) => setFormData({ ...formData, outbound_email_mode: e.target.value })}
                  className="rounded-full border-gray-300"
                />
                <span className="text-sm">Create Drafts (Gmail)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="send"
                  checked={formData.outbound_email_mode === 'send'}
                  onChange={(e) => setFormData({ ...formData, outbound_email_mode: e.target.value })}
                  className="rounded-full border-gray-300"
                />
                <span className="text-sm">Send Immediately</span>
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              This sets the default mode when creating email campaigns. You can override this per campaign.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-2">
              Gmail From Address
            </label>
            <input
              type="email"
              value={formData.gmail_from_address}
              onChange={(e) => setFormData({ ...formData, gmail_from_address: e.target.value })}
              placeholder="your.email@gmail.com"
              className="w-full border rounded-md px-3 py-2 bg-background"
            />
            <p className="text-xs text-muted-foreground mt-2">
              The Gmail account to send emails from (requires OAuth connection in Integrations)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex items-center justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
        >
          <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default MatchingSettings;
